/**
 * EntityLink Component
 *
 * Renders an entity identifier (e.g., ORD-000056) as a clickable link
 * that navigates to the entity's detail page.
 *
 * Uses locale-aware routing via @/i18n/navigation Link.
 * DRY: Route resolution via chatbot.link-utils.ts (single source).
 *
 * Phase 19B: Interactive Entity Links
 *
 * @module EntityLink
 */

'use client';

import { memo } from 'react';
import { Box, Text } from '@axon/ui';
import { LuExternalLink } from 'react-icons/lu';

import { Link } from '@/i18n/navigation';

import { getEntityRoute, getTableNameForEntityId } from '../utils/chatbot.link-utils';

// ============================================================================
// Props
// ============================================================================

interface EntityLinkProps {
  /** Entity identifier (e.g., "ORD-000056") */
  entityId: string;
  /** Entity table name (e.g., "order_core"). If omitted, derived from entityId prefix. */
  tableName?: string;
  /** Optional display label. Defaults to entityId. */
  label?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * EntityLink component
 *
 * Renders an entity ID as a clickable navigation link.
 * Automatically resolves the route from the entity table name.
 * Falls back to plain text if no route is available.
 *
 * @example
 * <EntityLink entityId="ORD-000056" />
 * <EntityLink entityId="SHP-123" tableName="shipment_core" />
 * <EntityLink entityId="INV-100" label="Invoice #100" />
 */
export const EntityLink = memo(function EntityLink({
  entityId,
  tableName,
  label,
}: EntityLinkProps) {
  // Resolve table name from entityId prefix if not provided
  const resolvedTable = tableName ?? getTableNameForEntityId(entityId);
  const route = resolvedTable ? getEntityRoute(resolvedTable, entityId) : null;
  const displayText = label ?? entityId;

  // No route available — render as plain text
  if (!route) {
    return (
      <Text as="span" fontSize="inherit" color="inherit">
        {displayText}
      </Text>
    );
  }

  return (
    <Link
      href={route}
      aria-label={`Open ${entityId}`}
      title={`Open ${entityId}`}
      style={{ display: 'inline-flex' }}
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        gap="1"
        paddingX="0.5"
        paddingY="0.5"
        borderRadius="sm"
        cursor="pointer"
        color={{ base: 'blue.600', _dark: 'blue.300' }}
        textDecoration="underline"
        textDecorationColor={{ base: 'blue.200', _dark: 'blue.500' }}
        fontSize="inherit"
        _hover={{ textDecoration: 'none', background: 'blue.subtle' }}
      >
        {displayText}
        <Box as="span" flexShrink={0} display="inline-flex">
          <LuExternalLink size={12} />
        </Box>
      </Box>
    </Link>
  );
});

export default EntityLink;
